import { api } from '@/lib/axios';
import type {
  PaginatedUsers,
  InviteUserPayload,
  UpdateUserPayload,
  UpdateProfilePayload,
  ChangePasswordPayload,
  ListUsersParams,
  User,
} from '@/lib/users/user.types';

export const usersApi = {
  list: (params?: ListUsersParams) =>
    api.get<PaginatedUsers>('/users', { params }),

  invite: (payload: InviteUserPayload) =>
    api.post<{ message: string; userId: string }>('/users/invite', payload),

  acceptInvite: (token: string, password: string) =>
    api.post<{ message: string }>('/users/accept-invite', { token, password }),

  /** AP-38-B: Einladung in eine weitere Firma — nur für das eingeladene Konto lesbar. */
  accessInvite: (token: string) =>
    api.get<{ data: { tenantId: string; tenantName: string; angenommen: boolean } }>(`/users/access/${token}`),

  /** AP-38-C: Eine externe Person gibt ihren Zugang zu dieser Firma zurück. */
  leave: () =>
    api.post<{ data: { naechsteFirma: string | null } }>('/users/me/leave'),

  /** AP-38-B: Zugang zu einer weiteren Firma annehmen. */
  acceptAccess: (token: string) =>
    api.post<{ data: { tenantId: string; tenantName: string } }>(`/users/access/${token}/accept`),

  resendInvite: (id: string) =>
    api.post<{ message: string }>(`/users/${id}/resend-invite`),

  /** AP-38-F: Mitarbeiter ohne Zugang anlegen — ohne Anmeldekonto, Adresse freiwillig. */
  createWithoutAccess: (payload: { firstName: string; lastName: string; email: string | null; roleKey?: string }) =>
    api.post<{ data: User }>('/users/without-access', payload),

  /** AP-38-F: Einem Mitarbeiter ohne Zugang den Zugang geben — Einladung an seine Adresse. */
  grantAccess: (id: string, payload: { email: string | null }) =>
    api.post<{ message: string }>(`/users/${id}/grant-access`, payload),

  /** AP-38-E: Zwei-Faktor-Anmeldung zurücksetzen — nur ohne weitere Mitgliedschaft. */
  resetTwoFactor: (id: string) =>
    api.post<{ message: string }>(`/users/${id}/two-factor/reset`),

  update: (id: string, payload: UpdateUserPayload) =>
    api.patch<User>(`/users/${id}`, payload),

  changeEmail: (id: string, email: string) =>
    api.patch<User>(`/users/${id}/email`, { email }),

  /**
   * Löscht einen Benutzer endgültig und gibt seine E-Mail-Adresse frei.
   *
   * Der Server lehnt ab, sobald am Benutzer Daten hängen (Zeitbuchungen,
   * Monatsberichte, …) — mit einer Meldung, die sagt was. Für alle anderen Fälle
   * ist „Deaktivieren" das richtige Mittel.
   */
  remove: (id: string) => api.delete<{ message: string }>(`/users/${id}`),

  updateMyProfile: (payload: UpdateProfilePayload) =>
    api.patch<User>('/users/me/profile', payload),

  changePassword: (payload: ChangePasswordPayload) =>
    api.post<{ message: string }>('/users/me/change-password', payload),
};
